import { useEffect, useState, useMemo, ReactNode, Fragment, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  PaginationState,
  getCoreRowModel,
  getSortedRowModel,
  ColumnDef,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
  RowSelectionState,
  createColumnHelper,
  ColumnOrderState,
  SortingState,
} from '@tanstack/react-table';
import { IManualMessage } from './ManualTable';
import IndeterminateCheckbox, { HeaderIndeterminateCheckbox } from 'components/Table//util/IndeterminateCheckbox';
import StatusTag from 'pages/Setting/common/StatusTag';
import { SelectorContainer, SelectorItem } from 'components/Selector/style';
import { Stack } from 'components/Stack';
import { useCheckedCustomerInfos, ICustomerInfo } from './ManualStore';
import { AlertModal } from 'components/Modal/AlertModal';
import { Text } from 'components/Typography';
import { Button } from 'components/Button';
import { TableForManual } from './TableForManual';
import { validateOrderPriority, validateOrderStatus } from 'util/validate/validateStatus';
import { TOrderPriority, TOrderStatus } from 'constants/types';
import { Tag, TTagStatusKeys } from 'components/Tag';
import { TDragablePickerOptions } from 'components/Popup/DragablePicker';
import TableColumnOrderControlModal from 'components/Modal/TableColumnOrderControlModal';
import { ReactComponent as IcSetting } from 'constants/icon/ic_setting.svg';
import theme from 'constants/theme';
import { IconMarker } from 'components/Table/util/IconPalette';
import { shipment_type } from 'constants/commons';
import { ReactComponent as High } from 'constants/icon/ic_priority_high.svg';
import { ReactComponent as Low } from 'constants/icon/ic_priority_low.svg';
import { ReactComponent as Medium } from 'constants/icon/ic_priority_medium.svg';
import { ReactComponent as IconTruck } from 'constants/icon/ic_truck.svg';
import { IMultiSkillData } from 'types/masterData/multiSkill';
import dayjs from 'dayjs';
import { RowDirectionContainer } from 'components/Table/style';
import { IDB_Names } from 'constants/IDB';
import setupIndexedDB, { useIndexedDBStore } from 'hooks/useIndexedDB';

type Props = {
  data: IManualMessage[];
  isFetching: boolean;
};

const setting_ManualTableComponent_columns_content: TDragablePickerOptions = [
  // { key: 'order_id', name: '주문 ID', isSelected: true },
  { key: 'order_status', name: '주문 상태', isSelected: true },
  { key: 'customer_name', name: '고객명', isSelected: true },
  { key: 'customer_phone', name: '고객 연락처', isSelected: true },
  { key: 'driver_name', name: '담당 기사님', isSelected: true },
  { key: 'company_order_id', name: '업체주문번호', isSelected: true },
  { key: 'order_date', name: '주문 접수일', isSelected: true },
  { key: 'address', name: '주소', isSelected: true },
  { key: 'detail_address', name: '상세주소', isSelected: true },
  { key: 'item_name', name: '아이템명', isSelected: true },
  { key: 'item_count', name: '아이템 수량', isSelected: true },
  // 추가 처리 부분
  { key: 'desiredDate', name: '작업 희망일', isSelected: false },
  { key: 'shipmentType', name: '주문 유형', isSelected: false },
  { key: 'priority', name: '배차 우선순위', isSelected: false },
  { key: 'serviceVehicle', name: '담당 드라이버 지정', isSelected: false },
  { key: 'desiredTimeEnd', name: '희망 시간(이전)', isSelected: false },
  { key: 'desiredTimeStart', name: '희망 시간(이후)', isSelected: false },
  { key: 'capacity', name: '예상 용적량', isSelected: false },
  { key: 'skill', name: '특수 조건', isSelected: false },
  { key: 'serviceTime', name: '예상 작업 소요 시간 (분)', isSelected: false },
  { key: 'actualServiceTime', name: '실제 작업 소요 시간 (분)', isSelected: false },
  { key: 'completedAt', name: '작업 완료 일시', isSelected: false },
  { key: 'skippedComment', name: '보류 사유', isSelected: false },
  { key: 'skippedAt', name: '보류 일시', isSelected: false },
  { key: 'consignorName', name: '화주사 명', isSelected: false },
  { key: 'consignorPhone', name: '화주사 연락처', isSelected: false },
  { key: 'salesmanName', name: '중개사 명', isSelected: false },
  { key: 'salesmanPhone', name: '중개사 연락처', isSelected: false },
  { key: 'consigneeNote', name: '고객전달사항', isSelected: false },
  { key: 'note1', name: '비고 1', isSelected: false },
  { key: 'note2', name: '비고 2', isSelected: false },
  { key: 'note3', name: '비고 3', isSelected: false },
  { key: 'note4', name: '비고 4', isSelected: false },
  { key: 'note5', name: '비고 5', isSelected: false },
];

const ManualTableComponent = ({ data, isFetching }: Props) => {
  const navigate = useNavigate();
  const { checkedCustomerInfos, setCheckedCustomerInfos } = useCheckedCustomerInfos();
  const [defaultPagination, setDefaultPagination] = useState<TPaginationState>({ pageIndex: 0, pageSize: 10 });

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({}); // 0: true, 1: true, 2: true 이런식

  const columnHelper = createColumnHelper<any>();
  const [anchorIsOpen, setAnchorIsOpen] = useState<boolean>(false);
  const [anchorPoint, setAnchorPoint] = useState<DOMRect>();

  // 조회항목 설정 처리 관련
  const [TableColumnOrderControlModalIsOpen, setTableColumnOrderControlModalIsOpen] = useState<boolean>(false);
  const [tableColumnSettingOptions, setTableColumnSettingOptions] = useState<TDragablePickerOptions>(
    setting_ManualTableComponent_columns_content
  );
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [sorting, setSorting] = useState<SortingState>([]);

  // 조회항목 indexedDB 관련
  const { update, getOneByKey } = useIndexedDBStore(IDB_Names.user.stores[0].name);

  const setColumnOrderInIDB = async (memory: TDragablePickerOptions) => {
    let IDB_CurrentUser = (await getOneByKey('userId', parseInt(window.localStorage.getItem('userId') ?? '-33'))) as {
      [key: string]: any;
    };

    if (!IDB_CurrentUser) {
      return;
    } else {
      // messageManualSendTableColumnsSetting 객체에 memory 객체로 저장
      IDB_CurrentUser.messageManualSendTableColumnsSetting = {
        ...IDB_CurrentUser.messageManualSendTableColumnsSetting,
        memory,
      };

      update({ ...IDB_CurrentUser });
    }
  };

  const getIDBColumn = async () => {
    const targetUserId = window.localStorage.getItem('userId');
    if (!targetUserId) return;
    else {
      let res = await (getOneByKey('userId', parseInt(targetUserId)) as any);

      const updater = (res?.messageManualSendTableColumnsSetting?.memory as TDragablePickerOptions)?.map((d, i) => {
        if (d.name !== setting_ManualTableComponent_columns_content.find(x => x.key === d.key)?.name) {
          return {
            ...d,
            name: setting_ManualTableComponent_columns_content.find(x => x.key === d.key)?.name,
          };
        } else {
          return d;
        }
      });

      console.log(updater, 'updater');

      if (updater) setTableColumnSettingOptions(updater as TDragablePickerOptions);
    }
  };

  useEffect(() => {
    setupIndexedDB({
      databaseName: IDB_Names.user.DB_Name,
      version: IDB_Names.user.version,
      stores: IDB_Names.user.stores,
    })
      .then(() => {
        console.log('successfully set');
      })
      .catch(e => console.error('error / unsupported', e));
  }, []);

  useEffect(() => {
    getIDBColumn();
  }, []);

  // 모달 관련
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [messageCount, setMessageCount] = useState<number | null>(null);
  // 체크 시 수신인 전번 없을 때 모달 띄우기 (수신인 없는 부분은 메시지 컴포넌트에서 처리함)
  const [isNoPhoneModalOpen, setIsNoPhoneModalOpen] = useState<boolean>(false);

  // checkedCustomerInfos 가 변경되면 rowSelection 에 변경된 데이터를 반영(테이블의 체크박스 상태 변경관리)
  useEffect(() => {
    let t: any = {};
    checkedCustomerInfos.forEach(x => {
      let filtered = instance.getSortedRowModel().flatRows.filter(d => d.original?.customer_phone === x.customer_phone);
      if (filtered.length > 0) {
        filtered.forEach(f => {
          t[`${f.id}`] = true;
        });
      }
    });
    setRowSelection(t);
  }, [setRowSelection, checkedCustomerInfos]);

  useEffect(() => {
    return () => {
      // 다른 페이지로 이동시 초기화
      setCheckedCustomerInfos([]);
    };
  }, []);

  // 조회항목 설정 모달에서 컬럼 설정 후 적용
  useEffect(() => {
    let columnVisible: { [key: string]: boolean } = {
      order_id: true,
    };
    tableColumnSettingOptions.forEach(d => {
      d.hidden ? (columnVisible[`${d.key}`] = false) : (columnVisible[`${d.key}`] = d.isSelected);
    });
    setColumnVisibility(columnVisible);
    setColumnOrder(['select', 'order_id'].concat(tableColumnSettingOptions.map(d => `${d.key}`)));
  }, [tableColumnSettingOptions]);

  // 컬럼 정의
  const columns = useMemo(
    () => [
      // customer_phone 로 처리 useEffect 에서 customer_phone 로 체크한 데이터 관리하기
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <HeaderIndeterminateCheckbox
            {...{
              checked: table.getIsAllPageRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: event => {
                const sortedRows = table.getSortedRowModel().flatRows;
                // 1. 해당 페이지의 원데이터 가져오기 defaultPagination.pageIndex+1 * defaultPagination.pageSize
                const currentPageRowData = sortedRows
                  .slice(
                    defaultPagination.pageIndex * defaultPagination.pageSize,
                    (defaultPagination.pageIndex + 1) * defaultPagination.pageSize
                  )
                  .map(x => x.original);
                // 2. 1번에서 가져온 데이터가 전부 체크되어 있는지 확인
                const isAllChecked = currentPageRowData.every(x =>
                  // checkedRows.some(y => y.customer_phone === x.customer_phone)
                  checkedCustomerInfos.some(y => y.customer_phone === x.customer_phone)
                );
                // 3. 2번에서 전부 체크되어 있으면 체크 해제, 아니면 전부 체크
                if (isAllChecked) {
                  // 해당 checkedCustomerInfos 에서 현재 페이지 데이터 제거
                  const currentPageCustomerInfos = currentPageRowData.map(x => {
                    return {
                      customer_phone: x.customer_phone,
                      customer_name: x.customer_name,
                    };
                  });
                  const filteredCustomerInfos = checkedCustomerInfos.filter(
                    x => !currentPageCustomerInfos.some(y => y.customer_phone === x.customer_phone)
                  );
                  // 중복제거
                  const uniqueCustomerInfos = filteredCustomerInfos.filter(
                    (v, i, a) => a.findIndex(t => t.customer_phone === v.customer_phone) === i
                  );
                  setCheckedCustomerInfos(uniqueCustomerInfos);
                } else {
                  // 해당 checkedCustomerInfos 에 현재 페이지 데이터 추가
                  const currentPageCustomerInfos = currentPageRowData.map(x => {
                    return {
                      customer_phone: x.customer_phone,
                      customer_name: x.customer_name,
                    };
                  });
                  const filteredCustomerInfos = checkedCustomerInfos.concat(
                    currentPageCustomerInfos.filter(
                      x => !checkedCustomerInfos.some(y => y.customer_phone === x.customer_phone)
                    )
                  );
                  // 중복제거
                  const uniqueCustomerInfos = filteredCustomerInfos.filter(
                    (v, i, a) => a.findIndex(t => t.customer_phone === v.customer_phone) === i
                  );
                  // 전번이 없을 때 모달 띄우기
                  const filteredDataNoPhone = currentPageCustomerInfos.filter(item => item.customer_phone !== '');
                  if (filteredDataNoPhone.length === 0) {
                    setIsNoPhoneModalOpen(true);
                  }
                  setCheckedCustomerInfos(uniqueCustomerInfos);
                }

                return table.getToggleAllPageRowsSelectedHandler()(event);
              },
              anchorIsOpen,
              setAnchorIsOpen,
              setAnchorPoint,
            }}
          />
        ),
        cell: ({ row }) => {
          const isChecked = checkedCustomerInfos.some(item => item.customer_phone === row.original.customer_phone);
          // 전화번호 없는 데이터는 체크박스 비활성화 + regex 처리 해줘야함
          const phoneRegex = /^(01[016789]-?\d{3,4}-?\d{4})|(0\d{1,2}-?\d{3,4}-?\d{4})$/;
          const isDisabled = !phoneRegex.test(row.original.customer_phone);
          return (
            <IndeterminateCheckbox
              {...{
                // checked: row.getIsSelected(),
                checked: isChecked,
                disabled: isDisabled,
                indeterminate: row.getIsSomeSelected(),
                onChange: event => {
                  // 체크한 데이터가 checkedCustomerInfos 에 포함되어 있는지 확인
                  const isIncluded = checkedCustomerInfos.some(
                    item => item.customer_phone === row.original.customer_phone
                  );
                  if (isIncluded) {
                    // checkedCustomerInfos 에서 해당 데이터 제거
                    setCheckedCustomerInfos(
                      checkedCustomerInfos.filter(item => item.customer_phone !== row.original.customer_phone)
                    );
                  } else {
                    // 전체 데이터에서 customer_phone 가 같은 데이터 전부 추가처리
                    setCheckedCustomerInfos([
                      ...checkedCustomerInfos,
                      {
                        customer_phone: row.original.customer_phone,
                        customer_name: row.original.customer_name,
                      },
                    ]);
                    const sameCustomerData = data.filter(x => x.customer_phone === row.original.customer_phone);
                    if (sameCustomerData.length > 1) {
                      setIsModalOpen(true);
                      setMessageCount(sameCustomerData.length);
                    }
                  }
                  return row.getToggleSelectedHandler()(event);
                },
              }}
              onClick={e => {
                e.stopPropagation();
              }}
            />
          );
        },
        enableSorting: false,
      }),
      columnHelper.accessor(row => row.order_id, {
        id: 'order_id',
        header: '주문 ID',
        footer: info => info.column.id,
      }),
      columnHelper.accessor(row => row.order_status, {
        id: 'order_status',
        header: '주문 상태',
        cell: info => {
          if (info.getValue()) {
            let { key, name } = validateOrderStatus(info.getValue() as TOrderStatus) ?? {
              key: 'key',
              name: 'name',
            };

            return (
              <>
                <div className="hoverShow">
                  <Tag active={true} styleName="caption2" status={key as TTagStatusKeys} sx={{ width: '56px' }}>
                    {name}
                  </Tag>
                </div>
                <Tag active={false} styleName="caption2" status={key as TTagStatusKeys} sx={{ width: '56px' }}>
                  {name}
                </Tag>
              </>
            );
          } else return null;
        },
        footer: info => info.column.id,
      }),
      columnHelper.accessor(row => row.customer_name, {
        id: 'customer_name',
        header: '고객명',
        footer: info => info.column.id,
      }),
      columnHelper.accessor(row => row.customer_phone, {
        id: 'customer_phone',
        header: '고객 연락처',
        cell: info => {
          return info.getValue() ? info.getValue().replace(/(\d{2,3})(\d{4})(\d{4})/, '$1-$2-$3') : '';
        },
        footer: info => info.column.id,
      }),
      columnHelper.accessor(
        row => {
          if (row.product) {
            if (row.product.length >= 2)
              return `${
                row.product.find((d: { consignorName?: string }) => d.consignorName)?.consignorName ?? `-`
              } 외 ${row.product.length - 1}건`;
            else return `${row.product[0].consignorName ?? `-`}`;
          }
          return '-';
        },
        {
          id: 'consignorName',
          header: () => '화주사 명',
          footer: info => info.column.id,
        }
      ),
      columnHelper.accessor(
        row => {
          if (row.product) {
            if (row.product.length >= 2)
              return `${
                row.product.find((d: { consignorPhone?: string }) => d.consignorPhone)?.consignorPhone ?? `-`
              } 외 ${row.product.length - 1}건`;
            else return `${row.product[0].consignorPhone ?? `-`}`;
          }
          return '-';
        },
        {
          id: 'consignorPhone',
          header: () => '화주사 연락처',
          footer: info => info.column.id,
        }
      ),
      columnHelper.accessor(
        row => {
          if (row.product) {
            if (row.product.length >= 2)
              return `${row.product.find((d: { salesmanName?: string }) => d.salesmanName)?.salesmanName ?? `-`} 외 ${
                row.product.length - 1
              }건`;
            else return `${row.product[0].salesmanName ?? `-`}`;
          }
          return '-';
        },
        {
          id: 'salesmanName',
          header: () => '중개사 명',
          footer: info => info.column.id,
        }
      ),
      columnHelper.accessor(
        row => {
          if (row.product) {
            if (row.product.length >= 2)
              return `${
                row.product.find((d: { salesmanPhone?: string }) => d.salesmanPhone)?.salesmanPhone ?? `-`
              } 외 ${row.product.length - 1}건`;
            else return `${row.product[0].salesmanPhone ?? `-`}`;
          }
          return '-';
        },
        {
          id: 'salesmanPhone',
          header: () => '중개사 연락처',
          footer: info => info.column.id,
        }
      ),
      columnHelper.accessor(row => row.driver_name, {
        id: 'driver_name',
        header: '담당 기사님',
        footer: info => info.column.id,
      }),
      columnHelper.accessor(row => row.company_order_id, {
        id: 'company_order_id',
        header: '업체주문번호',
        footer: info => info.column.id,
      }),
      columnHelper.accessor(row => row.order_date, {
        id: 'order_date',
        header: '주문 접수일',
        footer: info => info.column.id,
      }),
      columnHelper.accessor(row => row.address, {
        id: 'address',
        header: '주소',
        footer: info => info.column.id,
      }),
      columnHelper.accessor(row => row.detail_address, {
        id: 'detail_address',
        header: '상세주소',
        footer: info => info.column.id,
      }),
      columnHelper.accessor(row => row.item_name, {
        id: 'item_name',
        header: '아이템명',
        footer: info => info.column.id,
      }),
      columnHelper.accessor(row => row.item_count, {
        id: 'item_count',
        header: '아이템 수량',
        footer: info => info.column.id,
      }),
      // // 추가 처리 부분
      // 작업 희망일
      columnHelper.accessor(row => row.desiredDate, {
        id: 'desiredDate',
        header: '작업 희망일',
        footer: info => info.column.id,
      }),
      // 주문 유형 shipmentType
      columnHelper.accessor(row => row.shipmentType, {
        id: 'shipmentType',
        header: '주문 유형',
        cell: info => {
          let rowShipmentType = info.getValue();
          return (
            <Stack direction="row" sx={{ margin: '0 -9px' }}>
              <Fragment>
                <RowDirectionContainer>
                  <div className="hoverShow">
                    {createElement(
                      shipment_type.find(d => d.value === rowShipmentType?.replace('Rotation', ''))?.icon['ff'] ??
                        'div',
                      {
                        width: 20,
                        height: 20,
                      }
                    )}
                  </div>
                  {createElement(
                    shipment_type.find(d => d.value === rowShipmentType?.replace('Rotation', ''))?.icon['pf'] ?? 'div',
                    {
                      width: 20,
                      height: 20,
                    }
                  )}
                </RowDirectionContainer>

                {rowShipmentType?.includes('Rotation') && (
                  <RowDirectionContainer>
                    <div className="hoverShow">
                      {createElement(shipment_type.find(d => d.value === 'rotation')?.icon['ff'] ?? 'div', {
                        width: 20,
                        height: 20,
                      })}
                    </div>
                    {createElement(shipment_type.find(d => d.value === 'rotation')?.icon['pf'] ?? 'div', {
                      width: 20,
                      height: 20,
                    })}
                  </RowDirectionContainer>
                )}
              </Fragment>
            </Stack>
          );
        },
        footer: info => info.column.id,
      }),
      // 배차 운선순위  priority
      columnHelper.accessor(row => row?.priority, {
        id: 'priority',
        header: '배차 우선순위',
        cell: info => {
          let { key, name } = validateOrderPriority(info.getValue() as TOrderPriority) ?? { key: 'key', name: 'name' };
          return info.row.original.type !== 'order' ? null : (
            <div
              key={key as TTagStatusKeys}
              style={{
                display: 'flex',
                justifyContent: 'left',
                alignItems: 'center',
              }}
            >
              {name === '높음' ? <High width={14} height={14} /> : null}
              {name === '보통' ? <Medium width={14} height={14} /> : null}
              {name === '낮음' ? <Low width={14} height={14} /> : null}
              <Tag
                active={true}
                styleName="caption2"
                status={'done'}
                sx={{
                  width: '100%',
                  maxWidth: '34px',
                  minWidth: '34px',
                  color: '#25282F',
                  background: 'white',
                  border: '1px solid #D8DDEA',
                  borderRadius: '6px',
                  marginRight: '0',
                  marginLeft: '8px',
                }}
              >
                {name}
              </Tag>
            </div>
          );
        },
      }),
      // 담당 드라이버 지정 serviceVehicle
      columnHelper.accessor(
        row => {
          return row?.serviceVehicle ?? null;
        },
        {
          id: 'serviceVehicle',
          header: () => '담당 드라이버 지정',
          cell: info => {
            return info.getValue() ? <IconMarker size={[20, 12.5]} icon={<IconTruck />} /> : null;
          },
          enableSorting: false,
        }
      ),
      // 희망 시간(이전) desiredTimeEnd
      columnHelper.accessor(row => (row?.desiredTimeEnd ? row.desiredTimeEnd : '-'), {
        id: 'desiredTimeEnd',
        header: '희망 시간(이전)',
        footer: info => info.column.id,
      }),
      // 희망 시간(이후) desiredTimeStart
      columnHelper.accessor(row => (row?.desiredTimeStart ? row.desiredTimeStart : '-'), {
        id: 'desiredTimeStart',
        header: '희망 시간(이후)',
        footer: info => info.column.id,
      }),
      // 예상 용적량 capacity
      columnHelper.accessor(row => row.capacity, {
        id: 'capacity',
        header: '예상 용적량',
        footer: info => info.column.id,
      }),
      // 특수 조건 skills
      columnHelper.accessor(
        (row: { skills?: Pick<IMultiSkillData, 'skillId' | 'name'>[] }) =>
          Array.isArray(row?.skills) && row.skills.length ? row.skills.map(skill => skill.name).join(', ') : '-',
        {
          id: 'skill',
          header: `특수 조건`,
          footer: (info: { column: { id: string } }) => info.column.id,
        }
      ),
      // 예상 작업 소요 시간 (분) serviceTime
      columnHelper.accessor(row => row.serviceTime, {
        id: 'serviceTime',
        header: '예상 작업 소요 시간 (분)',
        footer: info => info.column.id,
      }),
      // 실제 작업 소요 시간 (분) actualServiceTime
      columnHelper.accessor(row => (row.actualServiceTime ? Math.floor(row.actualServiceTime / 60) + '분' : '-'), {
        id: 'actualServiceTime',
        header: '실제 작업 소요 시간 (분)',
        footer: info => info.column.id,
      }),
      // 작업 완료 일시 completedAt
      columnHelper.accessor(row => (row.completedAt ? dayjs(row.completedAt).format('YYYY.MM.DD HH:mm') : '-'), {
        id: 'completedAt',
        header: '작업 완료 일시',
        footer: info => info.column.id,
      }),
      // 보류 사유 skippedComment
      columnHelper.accessor(row => (row.skippedComment ? row.skippedComment : '-'), {
        id: 'skippedComment',
        header: '보류 사유',
        footer: info => info.column.id,
      }),
      // 보류 일시 skippedAt
      columnHelper.accessor(row => (row.skippedAt ? dayjs(row.skippedAt).format('YYYY-MM-DD HH:mm') : '-'), {
        id: 'skippedAt',
        header: '보류 일시',
        footer: info => info.column.id,
      }),
      // 고객 전달 사항 consigneeNote
      columnHelper.accessor(row => (row?.consigneeNote ? row.consigneeNote : '-'), {
        id: 'consigneeNote',
        header: '고객전달사항',
        footer: info => info.column.id,
      }),
      columnHelper.accessor(row => (row.note?.at(0) ? row.note.at(0) : '-'), {
        id: 'note1',
        header: '비고 1',
        footer: info => info.column.id,
      }),
      columnHelper.accessor(row => (row.note?.at(1) ? row.note.at(1) : '-'), {
        id: 'note2',
        header: '비고 2',
        footer: info => info.column.id,
      }),
      columnHelper.accessor(row => (row.note?.at(2) ? row.note.at(2) : '-'), {
        id: 'note3',
        header: '비고 3',
        footer: info => info.column.id,
      }),
      columnHelper.accessor(row => (row.note?.at(3) ? row.note.at(3) : '-'), {
        id: 'note4',
        header: '비고 4',
        footer: info => info.column.id,
      }),
      columnHelper.accessor(row => (row.note?.at(4) ? row.note.at(4) : '-'), {
        id: 'note5',
        header: '비고 5',
        footer: info => info.column.id,
      }),
    ],
    [defaultPagination, rowSelection, data, checkedCustomerInfos]
  );

  const instance = useReactTable({
    data,
    columns,
    state: {
      pagination: defaultPagination,
      rowSelection,
      columnVisibility,
      sorting,
      columnOrder,
    },
    initialState: {},
    enableRowSelection: true, //enable row selection for all rows
    onSortingChange: setSorting,
    onColumnOrderChange: setColumnOrder,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <Fragment>
      <TableForManual
        checkedCustomerInfos={checkedCustomerInfos}
        tableRowCount={{
          totalCount: { text: '항목', unit: '개' },
          eachCount: { text: '항목', unit: '개' },
        }}
        table={instance}
        setRowSelection={setRowSelection}
        // onRowClick={(e, row) => {
        //   onRowClick && onRowClick(row);
        // }}
        pagination={defaultPagination}
        setPagination={setDefaultPagination}
        placeholder={
          <PlaceHolderDiv>
            <PlaceHolderText>
              아직 주문 내역이 없습니다.
              <br />
              먼저 '알림메시지 자동 설정'을 통해 알림 메시지 설정을 자동화 해보세요.
            </PlaceHolderText>
            <PlaceHolderButton
              onClick={() => {
                navigate('/manage/message/auto');
              }}
            >
              알림메시지 자동 설정으로 이동
            </PlaceHolderButton>
          </PlaceHolderDiv>
        }
        isDataFetching={isFetching}
        clearBottom
        sc={[
          <Button
            type="button"
            variant={'tertiary'}
            w={126}
            h={32}
            icon={[IcSetting, { width: 16, height: 16, fill: theme.colors.RG04 }]}
            onClick={() => setTableColumnOrderControlModalIsOpen(true)}
          >
            조회항목 설정
          </Button>,
        ]}
      />
      {anchorIsOpen && (
        <SelectorContainer
          spacing={10}
          sx={{
            width: 'fit-content',
            top: anchorPoint && anchorPoint.top - 26,
            left: anchorPoint && anchorPoint.left - 36,
          }}
        >
          <Stack>
            <SelectorItem
              onClick={() => {
                if (data.length > 0) {
                  const allCustomerInfos = data
                    .map(x => {
                      return {
                        customer_phone: x.customer_phone,
                        customer_name: x.customer_name,
                      };
                    }) // 중복제거
                    .filter((v, i, a) => a.findIndex(t => t.customer_phone === v.customer_phone) === i);
                  setCheckedCustomerInfos(allCustomerInfos);
                }
                setAnchorIsOpen(false);
              }}
            >
              전체 선택
            </SelectorItem>
            <SelectorItem
              onClick={() => {
                setCheckedCustomerInfos([]);
                setAnchorIsOpen(false);
              }}
            >
              전체 선택 해제
            </SelectorItem>
          </Stack>
        </SelectorContainer>
      )}
      <TableColumnOrderControlModal
        isOpen={TableColumnOrderControlModalIsOpen}
        setIsOpen={setTableColumnOrderControlModalIsOpen}
        options={tableColumnSettingOptions}
        setOptions={setTableColumnSettingOptions}
        initial={setting_ManualTableComponent_columns_content}
        setColumnOrderInIDB={setColumnOrderInIDB}
      />
      <AlertModal
        type="NONE"
        isOpen={isModalOpen && !!messageCount}
        messages={
          <Stack sx={{ gap: '8px' }}>
            <Text styleName="body2" color="RG02">
              중복 선택된 수신인이 {messageCount}명 감지되어
            </Text>
            <Text styleName="body2" color="RG02">
              1건씩 리스트에 통합 등록되었습니다.
            </Text>
          </Stack>
        }
        buttonGroup={
          <Button
            variant="tertiary"
            h={40}
            sx={{ width: '100%', marginTop: '40px' }}
            onClick={() => {
              setIsModalOpen(false);
              setMessageCount(null);
            }}
          >
            <Text styleName="body1" color="RG03">
              닫기
            </Text>
          </Button>
        }
      />
      <AlertModal
        type="NONE"
        isOpen={isNoPhoneModalOpen}
        messages={
          <Stack sx={{ gap: '8px' }}>
            <Text styleName="body2" color="RG02">
              추가할 수 있는 수신인이 없습니다.
            </Text>
          </Stack>
        }
        buttonGroup={
          <Button
            variant="tertiary"
            h={40}
            sx={{ width: '100%', marginTop: '40px' }}
            onClick={() => {
              setIsNoPhoneModalOpen(false);
            }}
          >
            <Text styleName="body1" color="RG03">
              닫기
            </Text>
          </Button>
        }
      />
    </Fragment>
  );
};

export default ManualTableComponent;

const PlaceHolderDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  flex: 1 0 0;
  align-self: stretch;
`;
const PlaceHolderText = styled.div`
  align-self: stretch;

  color: var(--GrayScale-gray700, #7d818d);
  text-align: center;
  font-feature-settings: 'liga' off, 'clig' off;

  /* body14M */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 171.429% */
  letter-spacing: -0.28px;
`;

const PlaceHolderButton = styled.button`
  display: flex;
  width: 216px;
  height: 40px;
  padding: 8px 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;

  border-radius: 8px;
  background: var(--Primary-Blue700, #2e66f6);

  color: var(--RG00, #fff);
  text-align: center;
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 150% */
  letter-spacing: -0.32px;
`;
